/**
 * screenData.ts — Screen content and configuration.
 * Extracted from renderScreen.tsx to reduce file size and improve SRP.
 * Contains all retreat options, discovery catalogs, and screen metadata.
 */

import {
  halfDayRetreat,
  oneDayRetreat,
  twoDayRetreat,
  threeDayRetreat,
  twoMinRetreat,
  twoHourRetreat,
  weekendIntroRetreat,
  sevenDaySessionhin,
  oneDayVipassana,
  threeDayVipassana,
  tenDayVipassana,
} from "@retreats/content";
import type { EditableRetreatSchedule } from "@retreat/core";

export interface DiscoverOption {
  schedule: EditableRetreatSchedule;
  label: string;
  tradition: "zen" | "vipassana" | "secular";
  traditionLabel: string;
  summary: string | null;
  goodFor: { id: string; label: string }[];
}

export interface SelectionOption {
  schedule: EditableRetreatSchedule;
  label: string;
  description: string;
}

/** Zen selection options for quick-start flow */
export const ZEN_SELECTION_OPTIONS: SelectionOption[] = [
  {
    schedule: halfDayRetreat,
    label: "Half day",
    description: "5.5 hours · SFZC Saturday style",
  },
  {
    schedule: oneDayRetreat,
    label: "One day",
    description: "7 hours · Hokyoji one-day retreat",
  },
  {
    schedule: twoDayRetreat,
    label: "Two days",
    description: "~21 hours · full day + closing morning",
  },
  {
    schedule: threeDayRetreat,
    label: "Three days",
    description: "~31 hours · opening evening, full day, closing",
  },
];

/** Full discovery catalog: all available retreats organized by tradition */
export const DISCOVER_OPTIONS: DiscoverOption[] = [
  // ── Sōtō Zen ─────────────────────────────────────────────────────────────────
  {
    schedule: twoMinRetreat,
    label: "First sit · 2 min",
    tradition: "zen",
    traditionLabel: "Sōtō Zen",
    summary: "A single bell, a breath, and two minutes of stillness. Your first taste of the form.",
    goodFor: [{ id: "first-timers", label: "first-timers" }, { id: "gentle", label: "gentle" }],
  },
  {
    schedule: twoHourRetreat,
    label: "Morning sitting · 2 hrs",
    tradition: "zen",
    traditionLabel: "Sōtō Zen",
    summary: "Zazen, kinhin, and a short teaching. A complete morning practice at home.",
    goodFor: [{ id: "first-timers", label: "first-timers" }, { id: "gentle", label: "gentle" }],
  },
  {
    schedule: halfDayRetreat,
    label: "Half-day practice · 4 hrs",
    tradition: "zen",
    traditionLabel: "Sōtō Zen",
    summary: "SFZC Saturday-style. Four hours of sitting, walking, and work practice.",
    goodFor: [{ id: "gentle", label: "gentle" }],
  },
  {
    schedule: weekendIntroRetreat,
    label: "Weekend intro · 2 days",
    tradition: "zen",
    traditionLabel: "Sōtō Zen",
    summary: "Opening evening, full day of practice, closing. A gentle introduction.",
    goodFor: [{ id: "first-timers", label: "first-timers" }],
  },
  {
    schedule: oneDayRetreat,
    label: "One-day sesshin · 7 hrs",
    tradition: "zen",
    traditionLabel: "Sōtō Zen",
    summary: "Hokyoji one-day retreat: zazen, kinhin, chanting, work practice, and tea.",
    goodFor: [],
  },
  {
    schedule: twoDayRetreat,
    label: "Two-day retreat · 21 hrs",
    tradition: "zen",
    traditionLabel: "Sōtō Zen",
    summary: "Opening evening, full day of intensive practice, closing morning.",
    goodFor: [],
  },
  {
    schedule: threeDayRetreat,
    label: "Three-day sesshin · 31 hrs",
    tradition: "zen",
    traditionLabel: "Sōtō Zen",
    summary: "Opening evening, full day of intensive practice, closing morning.",
    goodFor: [],
  },
  {
    schedule: sevenDaySessionhin,
    label: "Seven-day sesshin · 6+ days",
    tradition: "zen",
    traditionLabel: "Sōtō Zen",
    summary: "A full-week intensive retreat at a traditional practice center.",
    goodFor: [],
  },
  // ── Vipassana ────────────────────────────────────────────────────────────────
  {
    schedule: oneDayVipassana,
    label: "One-day vipassana · 7 hrs",
    tradition: "vipassana",
    traditionLabel: "Vipassana",
    summary: "Following the ancient Mahasi Sayadaw method: body scan, noting, investigation.",
    goodFor: [{ id: "first-timers", label: "first-timers" }, { id: "gentle", label: "gentle" }],
  },
  {
    schedule: threeDayVipassana,
    label: "Three-day retreat · 31 hrs",
    tradition: "vipassana",
    traditionLabel: "Vipassana",
    summary: "Continuous practice: observation, inquiry, gentle instruction.",
    goodFor: [],
  },
  {
    schedule: tenDayVipassana,
    label: "Ten-day retreat · 10 days",
    tradition: "vipassana",
    traditionLabel: "Vipassana",
    summary: "The classic 10-day format: continuous meditation and self-observation.",
    goodFor: [],
  },
  // ── Secular ──────────────────────────────────────────────────────────────────
  {
    schedule: oneDayRetreat,
    label: "One-day meditation · 7 hrs",
    tradition: "secular",
    traditionLabel: "Modern secular",
    summary: "A straightforward day of meditation without ritual or cultural tradition.",
    goodFor: [{ id: "first-timers", label: "first-timers" }, { id: "gentle", label: "gentle" }],
  },
];

/** Links and resources */
export const EXTERNAL_LINKS = {
  TARGET: '_blank',
  REL: 'noopener,noreferrer',
} as const;

/** User agent patterns for platform detection */
export const UA_PATTERNS = {
  IOS_RE: /iPad|iPhone|iPod/,
} as const;
